import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { inventoryAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  ArrowLeft, Package, Plus, Layers, Calendar, DollarSign, 
  Receipt, Lock, Pencil, Check, X, Upload, FileText, Trash2
} from 'lucide-react';

const Batches = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [item, setItem] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Add batch dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({
    quantity: '',
    purchase_cost: '',
    purchase_date: new Date().toISOString().split('T')[0],
    lot_number: '',
    expiration_date: '',
    ndc_number: ''
  });
  const [invoiceFiles, setInvoiceFiles] = useState([]);  // Up to 3 images
  const [invoicePreviews, setInvoicePreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Edit batch dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [editForm, setEditForm] = useState({
    lot_number: '',
    expiration_date: '',
    purchase_cost: '',
    ndc_number: '',
    edit_note: ''
  });
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxTitle, setLightboxTitle] = useState('');

  const canManage = user?.role === 'clinic_admin' || user?.role === 'inventory_manager';

  useEffect(() => {
    fetchData();
  }, [itemId]);

  // Open add dialog if query param is set (then clear it)
  useEffect(() => {
    if (searchParams.get('add') === 'true' && item) {
      setAddDialogOpen(true);
      // Clear the query param to prevent reopening
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, item, setSearchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemRes, batchesRes] = await Promise.all([
        inventoryAPI.getById(itemId),
        inventoryAPI.getBatches(itemId, true)
      ]);
      setItem(itemRes.data);
      setBatches(batchesRes.data || []);
    } catch (error) {
      toast.error('Failed to load item data');
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone shifting
    if (dateStr.length === 10 && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toFixed(2)}`;
  };

  const handleInvoiceSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Invalid file type', { description: 'Please select a JPEG, PNG, WebP, or GIF image' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', { description: 'Maximum file size is 5MB' });
      return;
    }
    
    if (invoiceFiles.length >= 3) {
      toast.error('Maximum 3 images', { description: 'You can only upload up to 3 images per batch' });
      return;
    }
    
    setInvoiceFiles([...invoiceFiles, file]);
    const reader = new FileReader();
    reader.onloadend = () => setInvoicePreviews([...invoicePreviews, reader.result]);
    reader.readAsDataURL(file);
  };

  const removeInvoiceImage = (index) => {
    setInvoiceFiles(invoiceFiles.filter((_, i) => i !== index));
    setInvoicePreviews(invoicePreviews.filter((_, i) => i !== index));
  };

  const handleAddBatch = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        inventory_item_id: itemId,
        quantity: parseInt(batchForm.quantity),
        purchase_cost: parseFloat(batchForm.purchase_cost),
        purchase_date: batchForm.purchase_date || null,
        lot_number: batchForm.lot_number || null,
        expiration_date: batchForm.expiration_date || null,
        ndc_number: batchForm.ndc_number || null
      };
      
      const response = await inventoryAPI.addBatch(itemId, payload);
      
      // Upload invoice images if provided (up to 3)
      if (invoiceFiles.length > 0 && response.data?.id) {
        for (let i = 0; i < invoiceFiles.length; i++) {
          const formData = new FormData();
          formData.append('file', invoiceFiles[i]);
          formData.append('image_index', i.toString());
          await inventoryAPI.uploadBatchInvoice(itemId, response.data.id, formData);
        }
      }
      
      toast.success('Batch added successfully', {
        description: `Added ${batchForm.quantity} units @ ${formatCurrency(parseFloat(batchForm.purchase_cost))}/unit`
      });
      
      setAddDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add batch');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setBatchForm({
      quantity: '',
      purchase_cost: item?.unit_price?.toString() || '',
      purchase_date: new Date().toISOString().split('T')[0],
      lot_number: '',
      expiration_date: '',
      ndc_number: ''
    });
    setInvoiceFiles([]);
    setInvoicePreviews([]);
  };

  const openEditDialog = (batch) => {
    if (batch.locked) {
      toast.error('Cannot edit batch', { description: 'Stock has already been consumed from this batch' });
      return;
    }
    setEditingBatch(batch);
    setEditForm({
      lot_number: batch.lot_number || '',
      expiration_date: batch.expiration_date ? batch.expiration_date.split('T')[0] : '',
      purchase_cost: batch.purchase_cost.toString(),
      ndc_number: batch.ndc_number || '',
      edit_note: ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingBatch) return;
    
    setSavingEdit(true);
    try {
      const updateData = {};
      if (editForm.lot_number !== (editingBatch.lot_number || '')) {
        updateData.lot_number = editForm.lot_number || null;
      }
      if (editForm.expiration_date !== (editingBatch.expiration_date?.split('T')[0] || '')) {
        updateData.expiration_date = editForm.expiration_date || null;
      }
      if (parseFloat(editForm.purchase_cost) !== editingBatch.purchase_cost) {
        updateData.purchase_cost = parseFloat(editForm.purchase_cost);
      }
      if (editForm.ndc_number !== (editingBatch.ndc_number || '')) {
        updateData.ndc_number = editForm.ndc_number || null;
      }
      
      if (Object.keys(updateData).length === 0) {
        setEditDialogOpen(false);
        return;
      }

      // Add edit note to the update if provided
      if (editForm.edit_note) {
        updateData.edit_note = editForm.edit_note;
      }
      
      await inventoryAPI.updateBatch(itemId, editingBatch.id, updateData);
      toast.success('Batch updated successfully');
      setEditDialogOpen(false);
      setEditingBatch(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update batch');
    } finally {
      setSavingEdit(false);
    }
  };

  const [deletingBatch, setDeletingBatch] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleDeleteBatch = async () => {
    if (!editingBatch) return;
    setDeletingBatch(true);
    try {
      await inventoryAPI.deleteBatch(itemId, editingBatch.id);
      toast.success('Batch deleted successfully');
      setConfirmDeleteOpen(false);
      setEditDialogOpen(false);
      setEditingBatch(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    } finally {
      setDeletingBatch(false);
    }
  };


  const openLightbox = (imageUrl, title) => {
    setLightboxImage(imageUrl);
    setLightboxTitle(title);
    setLightboxOpen(true);
  };

  // Calculate summary stats
  const activeBatches = batches.filter(b => !b.is_depleted);
  const totalStock = activeBatches.reduce((sum, b) => sum + b.quantity_remaining, 0);
  const totalValue = activeBatches.reduce((sum, b) => sum + (b.quantity_remaining * b.purchase_cost), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="batches-page">
      {/* Header - matching Inventory page style */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/inventory')}
            className="text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">{item?.name}</h1>
            <p className="text-slate-500 text-sm mt-1">SKU: {item?.sku} • Stock Batches (FIFO)</p>
          </div>
        </div>
        {canManage && (
          <Button 
            onClick={() => { resetForm(); setAddDialogOpen(true); }}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md"
            data-testid="add-batch-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Stock
          </Button>
        )}
      </div>

      {/* Summary Cards - matching Inventory page style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Stock</p>
                <p className="text-2xl font-bold text-slate-900">{totalStock}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Avg Cost</p>
                <p className="text-2xl font-bold text-slate-900 font-mono">{formatCurrency(item?.average_cost)}</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Total Value</p>
                <p className="text-2xl font-bold text-slate-900 font-mono">{formatCurrency(totalValue)}</p>
              </div>
              <div className="p-2 bg-slate-100 rounded-lg">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-medium">Active Batches</p>
                <p className="text-2xl font-bold text-slate-900">{activeBatches.length} <span className="text-sm font-normal text-slate-400">/ {batches.length}</span></p>
              </div>
              <div className="p-2 bg-slate-100 rounded-lg">
                <Layers className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Batches Table - matching Inventory page table style */}
      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-600" />
            <h2 className="font-semibold text-slate-900">Batch History</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1">Stock is consumed from oldest batches first (FIFO - First In, First Out)</p>
        </div>
        
        {batches.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Layers className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No batches yet</p>
            <p className="text-sm mt-1">Add your first stock batch to get started</p>
            {canManage && (
              <Button 
                onClick={() => { resetForm(); setAddDialogOpen(true); }}
                className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Stock
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-slate-600 font-medium w-16">#</TableHead>
                  <TableHead className="text-slate-600 font-medium">Stock</TableHead>
                  <TableHead className="text-slate-600 font-medium">Cost/Unit</TableHead>
                  <TableHead className="text-slate-600 font-medium">Purchase Date</TableHead>
                  <TableHead className="text-slate-600 font-medium">Lot Number</TableHead>
                  <TableHead className="text-slate-600 font-medium">NDC</TableHead>
                  <TableHead className="text-slate-600 font-medium">Expiration</TableHead>
                  <TableHead className="text-slate-600 font-medium">Status</TableHead>
                  {canManage && <TableHead className="text-right text-slate-600 font-medium">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch, index) => {
                  const isFirstActive = index === batches.findIndex(b => !b.is_depleted);
                  
                  return (
                    <TableRow 
                      key={batch.id}
                      className={`${batch.is_depleted ? 'bg-slate-50 opacity-60' : isFirstActive ? 'bg-blue-50/50' : ''} hover:bg-slate-50`}
                    >
                      <TableCell>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${
                          batch.is_depleted 
                            ? 'bg-slate-200 text-slate-500' 
                            : isFirstActive 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {index + 1}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold text-slate-900">{batch.quantity_remaining}</span>
                        <span className="text-slate-400"> / {batch.quantity_purchased}</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-slate-700">{formatCurrency(batch.purchase_cost)}</span>
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(batch.purchase_date)}</TableCell>
                      <TableCell className="text-slate-600">{batch.lot_number || '-'}</TableCell>
                      <TableCell className="text-slate-600 font-mono text-xs">{batch.ndc_number || '-'}</TableCell>
                      <TableCell className="text-slate-600">{batch.expiration_date ? formatDate(batch.expiration_date) : '-'}</TableCell>
                      <TableCell>
                        {batch.is_depleted ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-200 text-slate-600">
                            Depleted
                          </span>
                        ) : isFirstActive ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            Active (FIFO)
                          </span>
                        ) : batch.locked ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <Lock className="w-3 h-3" />
                            Locked
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                            Available
                          </span>
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right space-x-1">
                          {(batch.invoice_image_urls?.length > 0 || batch.invoice_image_url) && (
                            <div className="inline-flex items-center gap-1">
                              {(batch.invoice_image_urls || [batch.invoice_image_url].filter(Boolean)).map((url, imgIndex) => (
                                <Button
                                  key={imgIndex}
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openLightbox(url, `Invoice ${imgIndex + 1} - Batch #${index + 1}`)}
                                  className="text-blue-600 hover:text-blue-700 px-1"
                                  title={`View Invoice ${imgIndex + 1}`}
                                >
                                  <Receipt className="w-4 h-4" />
                                </Button>
                              ))}
                            </div>
                          )}
                          {!batch.is_depleted && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(batch)}
                              disabled={batch.locked}
                              className={batch.locked ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:text-slate-900'}
                              title={batch.locked ? 'Locked: stock consumed' : 'Edit batch'}
                            >
                              {batch.locked ? <Lock className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Add Stock Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Stock</DialogTitle>
            <DialogDescription>
              Add a new purchase batch for <span className="font-medium">{item?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddBatch}>
            <DialogBody className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="quantity" className="text-slate-700">Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    value={batchForm.quantity}
                    onChange={(e) => setBatchForm({ ...batchForm, quantity: e.target.value })}
                    placeholder="e.g., 100"
                    required
                    min="1"
                    className="mt-1.5"
                    data-testid="batch-quantity"
                  />
                </div>
                <div>
                  <Label htmlFor="purchase_cost" className="text-slate-700">Cost/Unit ($) *</Label>
                  <Input
                    id="purchase_cost"
                    type="number"
                    step="0.01"
                    value={batchForm.purchase_cost}
                    onChange={(e) => setBatchForm({ ...batchForm, purchase_cost: e.target.value })}
                    placeholder="e.g., 5.50"
                    required
                    min="0.01"
                    className="mt-1.5"
                    data-testid="batch-cost"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="purchase_date" className="text-slate-700">Purchase Date *</Label>
                  <Input
                    id="purchase_date"
                    type="date"
                    value={batchForm.purchase_date}
                    onChange={(e) => setBatchForm({ ...batchForm, purchase_date: e.target.value })}
                    required
                    className="mt-1.5"
                    data-testid="batch-date"
                  />
                </div>
                <div>
                  <Label htmlFor="lot_number" className="text-slate-700">Lot Number</Label>
                  <Input
                    id="lot_number"
                    value={batchForm.lot_number}
                    onChange={(e) => setBatchForm({ ...batchForm, lot_number: e.target.value })}
                    placeholder="e.g., LOT-2025-001"
                    className="mt-1.5"
                    data-testid="batch-lot"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ndc_number" className="text-slate-700">NDC Number</Label>
                  <Input
                    id="ndc_number"
                    value={batchForm.ndc_number}
                    onChange={(e) => setBatchForm({ ...batchForm, ndc_number: e.target.value })}
                    placeholder="e.g., 12345-6789-01"
                    className="mt-1.5"
                    data-testid="batch-ndc"
                  />
                </div>
                <div>
                  <Label htmlFor="expiration_date" className="text-slate-700">Expiration Date</Label>
                  <Input
                    id="expiration_date"
                    type="date"
                    value={batchForm.expiration_date}
                    onChange={(e) => setBatchForm({ ...batchForm, expiration_date: e.target.value })}
                    className="mt-1.5"
                    data-testid="batch-expiration"
                  />
                </div>
              </div>
              
              {/* Invoice Upload - Up to 3 images */}
              <div>
                <Label className="text-slate-700">
                  Invoice Pictures <span className="text-slate-400 font-normal">({invoicePreviews.length}/3)</span>
                </Label>
                <div className="mt-1.5">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleInvoiceSelect}
                    className="hidden"
                    data-testid="batch-invoice-input"
                  />
                  <div className="flex flex-wrap gap-3 items-start">
                    {invoicePreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={preview} 
                          alt={`Invoice ${index + 1}`} 
                          className="w-24 h-24 object-cover rounded-lg border border-slate-300"
                        />
                        <button
                          type="button"
                          onClick={() => removeInvoiceImage(index)}
                          className="absolute -top-2 -right-2 bg-slate-600 text-white rounded-full p-1 hover:bg-slate-700"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {invoicePreviews.length < 3 && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                      >
                        <Upload className="w-5 h-5 mb-1" />
                        <span className="text-xs">Add</span>
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">JPEG, PNG, WebP, GIF. Max 5MB each. Up to 3 images.</p>
                </div>
              </div>
              
              {/* Summary */}
              {batchForm.quantity && batchForm.purchase_cost && (
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <span className="font-medium">Total Purchase Value: </span>
                    <span className="font-mono">{formatCurrency(parseFloat(batchForm.quantity) * parseFloat(batchForm.purchase_cost))}</span>
                  </p>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={submitting}
                data-testid="submit-batch"
              >
                {submitting ? (
                  <>
                    <span className="spinner mr-2"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Stock
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Batch Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Batch</DialogTitle>
            <DialogDescription>
              Update batch details for <span className="font-medium">{item?.name}</span>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEdit}>
            <DialogBody className="space-y-4">
              {/* Current batch info */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Stock</span>
                    <p className="font-semibold text-slate-900">{editingBatch?.quantity_remaining} / {editingBatch?.quantity_purchased}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Purchase Date</span>
                    <p className="font-semibold text-slate-900">{formatDate(editingBatch?.purchase_date)}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_purchase_cost" className="text-slate-700">Cost/Unit ($) *</Label>
                  <Input
                    id="edit_purchase_cost"
                    type="number"
                    step="0.01"
                    value={editForm.purchase_cost}
                    onChange={(e) => setEditForm({ ...editForm, purchase_cost: e.target.value })}
                    required
                    min="0.01"
                    className="mt-1.5"
                    data-testid="edit-batch-cost"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_lot_number" className="text-slate-700">Lot Number</Label>
                  <Input
                    id="edit_lot_number"
                    value={editForm.lot_number}
                    onChange={(e) => setEditForm({ ...editForm, lot_number: e.target.value })}
                    placeholder="e.g., LOT-2025-001"
                    className="mt-1.5"
                    data-testid="edit-batch-lot"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_ndc_number" className="text-slate-700">NDC Number</Label>
                  <Input
                    id="edit_ndc_number"
                    value={editForm.ndc_number}
                    onChange={(e) => setEditForm({ ...editForm, ndc_number: e.target.value })}
                    placeholder="e.g., 12345-6789-01"
                    className="mt-1.5"
                    data-testid="edit-batch-ndc"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_expiration_date" className="text-slate-700">Expiration Date</Label>
                  <Input
                    id="edit_expiration_date"
                    type="date"
                    value={editForm.expiration_date}
                    onChange={(e) => setEditForm({ ...editForm, expiration_date: e.target.value })}
                    className="mt-1.5"
                    data-testid="edit-batch-expiration"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit_note" className="text-slate-700">
                  Reason for Edit <span className="text-slate-400 font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="edit_note"
                  value={editForm.edit_note}
                  onChange={(e) => setEditForm({ ...editForm, edit_note: e.target.value })}
                  placeholder="e.g., Corrected cost after invoice review"
                  className="mt-1.5 resize-none"
                  rows={2}
                  data-testid="edit-batch-note"
                />
              </div>
            </DialogBody>
            <DialogFooter className="flex justify-between">
              <Button 
                type="button" 
                variant="destructive"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={deletingBatch || savingEdit}
                data-testid="delete-batch-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Batch
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white"
                  disabled={savingEdit || deletingBatch}
                  data-testid="save-batch-edit"
                >
                  {savingEdit ? (
                    <>
                      <span className="spinner mr-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Batch</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this batch and remove <span className="font-semibold text-slate-900">{editingBatch?.quantity_remaining} units</span> from stock. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingBatch}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBatch}
              disabled={deletingBatch}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-batch-btn"
            >
              {deletingBatch ? 'Deleting...' : 'Delete Batch'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div 
            className="max-w-4xl max-h-[85vh] animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxImage} 
              alt={lightboxTitle}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <p className="text-white text-center mt-4 text-lg font-medium">{lightboxTitle}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Batches;
